// ============================
// File: src/features/triage/TriageUI.jsx
// ============================
import React, { useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { DemandSchema } from "./schemas/demandSchema";
import { runTriageValidate, returnToSubmitter } from "./api/triageClient";
import ClarifyThread from "./components/ClarifyThread";
import SectionCard from "./components/SectionCard";
import { get as getJson, demandPath } from "../../api/http";

// Add animations
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
document.head.appendChild(style);

// ----------------------
// Helpers
// ----------------------
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function callAIWithRetry(demandId, payload, { maxRetries = 3, base = 400 } = {}) {
  let lastErr = null;
  for (let i = 0; i <= maxRetries; i++) {
    try {
      // NOTE: pass demandId to the API call
      const res = await runTriageValidate(demandId, payload);
      return res;
    } catch (err) {
      lastErr = err;
      // Retrying a 4xx just repeats the same rejection — and each attempt now runs
      // a Claude call server-side, so a bad payload would cost four of them.
      const status = err?.status;
      if (status && status >= 400 && status < 500) break;
      if (i === maxRetries) break;
      await sleep(base * Math.pow(2, i));
    }
  }
  throw lastErr || new Error("AI validation failed after retries");
}

function Badge({ color = "gray", children }) {
  const map = {
    gray: "bg-gray-100 text-gray-800",
    green: "bg-green-100 text-green-800",
    red: "bg-red-100 text-red-800",
    yellow: "bg-yellow-100 text-yellow-800",
    blue: "bg-blue-100 text-blue-800",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${map[color]}`}>
      {children}
    </span>
  );
}

function ROField({ label, value, multiline = false }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      {multiline ? (
        <div className="mt-1 whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
          {value || "—"}
        </div>
      ) : (
        <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-2 text-sm text-gray-800">
          {value || "—"}
        </div>
      )}
    </label>
  );
}

export default function TriageUI({ demandId } = {}) {
  const navigate = useNavigate();

  // Empty, not demo content. These used to be a fully populated fake demand
  // ("Modernize Case Intake Portal", owner Jane Smith, sponsor John Doe, IDEA-42),
  // and the loader below swallowed its errors with "keep default values if fetch
  // fails" — so a demand that failed to load showed somebody else's invented data
  // as if it were real, and the triage AI then scored that.
  const defaultValues = useMemo(
    () => ({
      demandId: demandId || "",
      title: "",
      description: "",
      businessOwner: "",
      sponsor: "",
      directorate: "",
      strategicTags: [],
      urgency: "Medium",
      expectedOutcomes: [],
      acceptanceCriteria: {},
      roiBenefit: "",
      attachments: [],
      ideaLinkage: {
        ideaId: "",
        aiSummary: "",
        similarityCluster: "",
        priorScores: [],
      },
    }),
    [demandId]
  );

  const methods = useForm({
    resolver: zodResolver(DemandSchema),
    defaultValues,
    mode: "onChange",
  });
  const { handleSubmit, watch, formState, reset } = methods;
  const values = watch();

  const [aiState, setAiState] = useState({
    status: "idle", // idle | running | passed | failed | pending_ai
    score: null,
    gaps: [],
    error: null,
  });

  const [threads, setThreads] = useState({});
  const [loadError, setLoadError] = useState("");
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [toast, setToast] = useState(null);

  // Toast helper
  const showToast = (message, type = 'success', onClick = null) => {
    setToast({ message, type, onClick });
    setTimeout(() => setToast(null), 8000);
  }; // fieldKey -> [{text, when}]
  const addThread = (fieldKey, text) => {
    setThreads((prev) => ({
      ...prev,
      [fieldKey]: [...(prev[fieldKey] || []), { text, when: new Date().toLocaleString() }],
    }));
  };

  // Reset aiState when demandId changes
  React.useEffect(() => {
    console.log('🔄 Demand ID changed to:', demandId, '- Resetting AI state');
    setAiState({
      status: "idle",
      score: null,
      gaps: [],
      error: null,
    });
    setThreads({});
  }, [demandId]);

  // Handle return to submitter
  const handleReturnToSubmitter = async () => {
    setIsReturning(true);
    try {
      console.log('🔙 Returning to submitter with gaps:', aiState.gaps);

      const result = await returnToSubmitter(demandId, {
        gap_list: aiState.gaps,
        triage_score: aiState.score
      });

      console.log('✅ Return to submitter successful:', result);

      // Close modal
      setShowReturnModal(false);

      // Show success toast with navigation option
      showToast(
        `✅ Demand returned to submitter! ${result.gap_count} gaps identified. Click here to edit the demand.`,
        'success',
        () => navigate(`/#demand-edit`)
      );

      // Reset AI state
      setAiState({
        status: "idle",
        score: null,
        gaps: [],
        error: null,
      });

    } catch (err) {
      console.error('❌ Failed to return to submitter:', err);
      showToast(`❌ Error: ${err.message || 'Failed to return to submitter'}`, 'error');
    } finally {
      setIsReturning(false);
    }
  };

  const onSubmit = async (data) => {
    console.log('🎯 onSubmit called!');
    console.log('📋 Form data:', data);

    // Client validations first (reflect Acceptance Criteria)
    const clientErrors = Object.keys(formState.errors);
    console.log('🔍 Client errors:', clientErrors);

    if (clientErrors.length) {
      console.log('❌ Validation failed, showing client errors');
      const errs = clientErrors.map((k) => ({
        field: k,
        issue: formState.errors[k]?.message || "Invalid",
        suggestion: "Please correct this field to proceed.",
      }));
      setAiState({ status: "failed", score: 0, gaps: errs, error: null });
      return;
    }

    console.log('✅ Validation passed, calling AI...');
    setAiState({ status: "running", score: null, gaps: [], error: null });

    try {
      const payload = {
        demandId: data.demandId,
        title: data.title,
        description: data.description,
        businessOwner: data.businessOwner,
        sponsor: data.sponsor,
        directorate: data.directorate,
        strategicTags: data.strategicTags,
        urgency: data.urgency,
        expectedOutcomes: data.expectedOutcomes,
        acceptanceCriteria: data.acceptanceCriteria,
        roiBenefit: data.roiBenefit,
        ideaLinkage: data.ideaLinkage,
      };

      const res = await callAIWithRetry(demandId, payload, { maxRetries: 3, base: 500 });

      // Extract triage_result from response
      const triageResult = res.triage_result || res;

      // No follow-up writes here. POST /triage/run already persisted the score,
      // gap list and stage transition; the writeDemandStatus / putTriageReport
      // calls that used to sit here targeted endpoints that don't exist and had
      // their 404s swallowed by a "non-fatal" catch.

      setAiState({
        status: triageResult.triage_pass ? "passed" : "failed",
        score: triageResult.triage_score,
        gaps: triageResult.gap_list || [],
        error: null,
      });
    } catch (err) {
      setAiState({ status: "pending_ai", score: null, gaps: [], error: err.message || "AI error" });
    }
  };

  const GapRow = ({ gap }) => (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-amber-900">{gap.field}</div>
        <Badge color="yellow">Flagged</Badge>
      </div>
      <div className="mt-1 text-sm text-amber-900">{gap.issue}</div>
      <div className="mt-1 text-sm text-amber-800 italic">AI Suggestion: {gap.suggestion}</div>
      <ClarifyThread fieldKey={gap.field} threads={threads[gap.field] || []} onAdd={addThread} />
    </div>
  );

  const DecisionPanel = () => {
    const { status, score, error } = aiState;
    return (
      <SectionCard
        title="Triage Decision"
        subtitle="Routing & status"
        right={
          status === "passed" ? (
            <Badge color="green">TRIAGE_PASSED</Badge>
          ) : status === "failed" ? (
            <Badge color="red">TRIAGE_FAILED</Badge>
          ) : status === "pending_ai" ? (
            <Badge color="blue">TRIAGE_PENDING_AI</Badge>
          ) : status === "running" ? (
            <Badge color="yellow">VALIDATING…</Badge>
          ) : (
            <Badge>Idle</Badge>
          )
        }
      >
        <div className="space-y-3">
          {typeof score === "number" && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">Triage Score</div>
              <div className="text-base font-semibold text-gray-900">{score} / 100</div>
            </div>
          )}

          {error && <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">{error}</div>}

          {aiState.status === "passed" && (
            <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
              Pass → Discovery. ITPM metrics event queued (aia.demand.triage.completed).
            </div>
          )}
          {aiState.status === "failed" && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              Fail → Returned to Submitter with ClarifyBot tasks. Resubmit loop allowed.
            </div>
          )}
          {aiState.status === "pending_ai" && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
              AI timeout. We will retry (x3) and alert ITPM.
            </div>
          )}
        </div>
      </SectionCard>
    );
  };

  React.useEffect(() => {
    if (!demandId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadError("");
        const result = await getJson(demandPath(demandId));
        const demand = result?.demand;
        if (cancelled) return;
        if (!demand) throw new Error("Response contained no demand record");

        // Parse strategic tags if stored as JSON string
        let strategicTags = [];
        try {
          strategicTags = typeof demand.strategic_tags === 'string'
            ? JSON.parse(demand.strategic_tags)
            : (demand.strategic_tags || []);
        } catch (e) {
          console.warn('Failed to parse strategic tags:', e);
        }

        // Parse expected outcomes (if stored as JSON)
        let expectedOutcomes = [];
        try {
          expectedOutcomes = typeof demand.expected_outcomes === 'string'
            ? JSON.parse(demand.expected_outcomes)
            : (demand.expected_outcomes || []);
        } catch (e) {
          expectedOutcomes = demand.expected_outcomes ? [demand.expected_outcomes] : [];
        }

        reset({
          demandId: demand.demand_id || demandId,
          title: demand.title || "",
          description: demand.description || "",
          businessOwner: demand.business_owner || "",
          sponsor: demand.sponsor || "",
          directorate: demand.directorate || "",
          strategicTags: strategicTags,
          urgency: demand.urgency || "Medium",
          expectedOutcomes: expectedOutcomes,
          acceptanceCriteria: {},
          roiBenefit: demand.initial_roi || "",
          attachments: [],
          ideaLinkage: {
            ideaId: demand.idea_id || "",
            aiSummary: demand.idea_purpose || "",
            similarityCluster: "",
            priorScores: [],
          },
        });
      } catch (e) {
        // Surfaced, not swallowed. Showing the empty form with an explanation is
        // honest; showing seeded demo content as if it were this demand is not.
        if (!cancelled) setLoadError(e.message || String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [demandId, reset]);

  return (
    <div className="mx-auto max-w-6xl p-6">
      {loadError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          Could not load demand {demandId}: {loadError}. The form below is empty —
          do not run triage against it.
        </div>
      )}
      <header className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 py-1">Triage Checklist</h1>
            <p className="text-sm text-gray-600">AI-powered validation to ensure demand is ready for Discovery.</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge color="blue">ITPM SLA: capture</Badge>
            <Badge>Demand: {values.demandId}</Badge>
          </div>
        </div>

        {/* PROMINENT ACTION BUTTONS AT TOP */}
        <div style={{
          backgroundColor: 'var(--cares-surface-board)',
          border: '2px solid var(--cares-line-card)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={async (e) => {
                console.log('🔵 BUTTON CLICKED!', e);
                console.log('🔵 aiState:', aiState);
                console.log('🔵 demandId:', demandId);
                console.log('🔵 Calling onSubmit directly...');
                e.preventDefault();
                e.stopPropagation();

                // Get current form values
                const currentValues = methods.getValues();
                console.log('🔵 Current form values:', currentValues);

                // Call onSubmit directly with form values
                await onSubmit(currentValues);
              }}
              disabled={aiState.status === "running"}
              style={{
                backgroundColor: aiState.status === "running" ? 'var(--cares-surface-raised)' : 'var(--cares-purple)',
                color: 'var(--cares-text-max)',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                border: 'none',
                cursor: aiState.status === "running" ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (aiState.status !== "running") {
                  e.target.style.backgroundColor = 'var(--cares-purple)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (aiState.status !== "running") {
                  e.target.style.backgroundColor = 'var(--cares-purple)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              {aiState.status === "running" ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '2px solid var(--cares-line-card)',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Validating with AI...
                </>
              ) : (
                <>🤖 Run AI Triage Validation</>
              )}
            </button>

            {aiState.status === "failed" && (
              <button
                type="button"
                onClick={() => setShowReturnModal(true)}
                style={{
                  backgroundColor: 'var(--cares-orange)',
                  color: 'var(--cares-text-max)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--cares-orange)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--cares-orange)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                ❌ Return to Submitter (Fix Gaps)
              </button>
            )}

            {aiState.status === "passed" && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Route this demand to Discovery stage?')) {
                    alert('TODO: Implement route to discovery - will update database and navigate to Discovery page');
                  }
                }}
                style={{
                  backgroundColor: 'var(--cares-green)',
                  color: 'var(--cares-text-max)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--cares-green)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--cares-green)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                ✅ Route to Discovery Stage
              </button>
            )}

            {/* Status indicator */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {aiState.status === "passed" && (
                <span style={{
                  backgroundColor: 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))',
                  color: 'var(--cares-green)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  ✓ TRIAGE PASSED
                </span>
              )}
              {aiState.status === "failed" && (
                <span style={{
                  backgroundColor: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
                  color: 'var(--cares-orange)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  ✗ TRIAGE FAILED
                </span>
              )}
              {typeof aiState.score === "number" && (
                <span style={{
                  backgroundColor: 'var(--cares-surface-board)',
                  color: 'var(--cares-text-2)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Score: {aiState.score}/100
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <SectionCard title="Demand Metadata" subtitle="Upstream & form inputs (read-only in triage)">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ROField label="Title" value={values.title} />
                <ROField label="Business Owner" value={values.businessOwner} />
                <ROField label="Sponsor" value={values.sponsor} />
                <ROField label="Directorate" value={values.directorate} />
                <ROField label="Urgency" value={values.urgency} />
                <ROField label="Strategic Tags" value={values.strategicTags.join(", ") || "—"} />
                <div className="sm:col-span-2">
                  <ROField label="Description" value={values.description} multiline />
                </div>
                <div className="sm:col-span-2">
                  <ROField label="Expected Outcomes" value={values.expectedOutcomes.join(" | ")} />
                </div>
                <div className="sm:col-span-2">
                  {/* show each criterion on a separate line */}
                  <ROField
                    label="Acceptance Criteria (per outcome)"
                    value={Object.entries(values.acceptanceCriteria)
                      .map(([k, v]) => `${k} → ${v}`)
                      .join("\n")}
                    multiline
                  />
                </div>
                <ROField label="ROI/Benefit" value={values.roiBenefit} />
              </div>
            </SectionCard>

            <SectionCard
              title="Gaps & Fixes"
              subtitle="AI validation flags with inline ClarifyBot threads"
              right={
                <Badge color={aiState.status === "failed" ? "red" : aiState.status === "passed" ? "green" : "yellow"}>
                  {aiState.status.toUpperCase()}
                </Badge>
              }
            >
              {aiState.status === "running" && (
                <div className="animate-pulse rounded-lg border border-gray-200 p-4">
                  <div className="h-3 w-1/3 rounded bg-gray-200" />
                  <div className="mt-3 h-3 w-2/3 rounded bg-gray-200" />
                </div>
              )}

              {aiState.gaps.length === 0 && aiState.status !== "failed" && aiState.status !== "passed" && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">Run triage to see AI flags.</div>
              )}

              <div className="space-y-3">
                {aiState.gaps.map((g, i) => (
                  <GapRow key={i} gap={g} />
                ))}
              </div>
            </SectionCard>
          </div>

          <div className="space-y-6">
            <DecisionPanel />

            {/* Actions moved to top - keeping this section for additional info */}
            <SectionCard title="Workflow Info" subtitle="Next steps based on triage results">
              <div className="space-y-3 text-sm">
                {aiState.status === "idle" && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-blue-900">
                    Click "Run AI Triage Validation" at the top to start the validation process.
                  </div>
                )}
                {aiState.status === "running" && (
                  <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-yellow-900">
                    AI is analyzing the demand for completeness, strategic alignment, and acceptance criteria...
                  </div>
                )}
                {aiState.status === "failed" && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-900">
                    <strong>Triage Failed:</strong> AI found gaps that need to be fixed. Click "Return to Submitter" to send back with gap list.
                  </div>
                )}
                {aiState.status === "passed" && (
                  <div className="rounded-md border border-green-200 bg-green-50 p-3 text-green-900">
                    <strong>Triage Passed:</strong> Demand is complete and ready for Discovery. Click "Route to Discovery" to proceed.
                  </div>
                )}
                {aiState.status === "pending_ai" && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-blue-900">
                    AI timeout. We will retry (x3) and alert ITPM if needed.
                  </div>
                )}
              </div>
            </SectionCard>

            <SectionCard title="Idea Linkage" subtitle="Context from upstream idea store">
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Idea ID:</span> {values.ideaLinkage?.ideaId || "—"}
                </div>
                <div>
                  <span className="font-medium">AI Summary:</span> {values.ideaLinkage?.aiSummary || "—"}
                </div>
                <div>
                  <span className="font-medium">Similarity Cluster:</span> {values.ideaLinkage?.similarityCluster || "—"}
                </div>
                <div>
                  <span className="font-medium">Prior Scores:</span> {values.ideaLinkage?.priorScores?.join(", ") || "—"}
                </div>
              </div>
            </SectionCard>
          </div>
        </form>
      </FormProvider>

      {/* Return to Submitter Modal */}
      {showReturnModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => !isReturning && setShowReturnModal(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--cares-surface-card)',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'var(--cares-orange)',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '32px' }}>⚠️</span>
                Return to Submitter
              </h2>
              <p style={{ color: 'var(--cares-text-label)', fontSize: '14px' }}>
                This will send the demand back to the business owner with a list of gaps to fix.
              </p>
            </div>

            {/* Gap List */}
            <div style={{
              backgroundColor: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
              border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--cares-orange)',
                marginBottom: '12px'
              }}>
                {aiState.gaps.length} Gap{aiState.gaps.length !== 1 ? 's' : ''} Identified
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {aiState.gaps.map((gap, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: 'var(--cares-surface-card)',
                      border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
                      borderRadius: '6px',
                      padding: '12px',
                    }}
                  >
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--cares-orange)',
                      marginBottom: '4px'
                    }}>
                      {gap.field}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--cares-text-3)', marginBottom: '4px' }}>
                      <strong>Issue:</strong> {gap.issue}
                    </div>
                    {gap.suggestion && (
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--cares-text-label)',
                        backgroundColor: 'var(--cares-surface-board)',
                        padding: '8px',
                        borderRadius: '4px',
                        marginTop: '8px'
                      }}>
                        <strong>Suggestion:</strong> {gap.suggestion}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Triage Score */}
            <div style={{
              backgroundColor: 'var(--cares-surface-board)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-3)' }}>
                Triage Score:
              </span>
              <span style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: aiState.score >= 70 ? 'var(--cares-green)' : 'var(--cares-orange)'
              }}>
                {aiState.score}/100
              </span>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleReturnToSubmitter}
                disabled={isReturning}
                style={{
                  flex: 1,
                  backgroundColor: isReturning ? 'var(--cares-surface-raised)' : 'var(--cares-orange)',
                  color: 'var(--cares-text-max)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: isReturning ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!isReturning) {
                    e.target.style.backgroundColor = 'var(--cares-orange)';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isReturning) {
                    e.target.style.backgroundColor = 'var(--cares-orange)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {isReturning ? '⏳ Returning...' : '✅ Confirm Return'}
              </button>
              <button
                onClick={() => setShowReturnModal(false)}
                disabled={isReturning}
                style={{
                  flex: 1,
                  backgroundColor: 'var(--cares-surface-board)',
                  color: 'var(--cares-text-3)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: isReturning ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!isReturning) {
                    e.target.style.backgroundColor = 'var(--cares-line-card)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isReturning) {
                    e.target.style.backgroundColor = 'var(--cares-surface-board)';
                  }
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div
          onClick={toast.onClick ? () => { toast.onClick(); setToast(null); } : undefined}
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            backgroundColor: toast.type === 'success' ? 'var(--cares-green)' : 'var(--cares-orange)',
            color: 'var(--cares-text-max)',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 10000,
            maxWidth: '500px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideIn 0.3s ease-out',
            cursor: toast.onClick ? 'pointer' : 'default',
          }}
          onMouseEnter={(e) => {
            if (toast.onClick) {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.2)';
            }
          }}
          onMouseLeave={(e) => {
            if (toast.onClick) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
            }
          }}
        >
          <span style={{ fontSize: '20px' }}>
            {toast.type === 'success' ? '✅' : '❌'}
          </span>
          <span>{toast.message}</span>
          <button
            onClick={(e) => { e.stopPropagation(); setToast(null); }}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: 'var(--cares-text-max)',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0 4px',
              opacity: 0.8,
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
